/**
 * iter-121.104 — Shared widget-derivation helpers.
 *
 * One canonical place for "what label should this chart series carry?"
 * and "what should the tooltip show on hover?". Every chart renderer
 * (legacy `Widgets.jsx`, prebuilt `prebuilt/presentation.jsx`, and the
 * local card in `PrebuiltRenderer.jsx`) must import from here.
 *
 * Why: the AI Builder's `update_widget` tool emits `options.legend_label`
 * + `options.tooltip_label`. Backend persists + propagates them. If a
 * renderer forgets to consult this helper, the rename silently breaks
 * for that chart type — exactly the "fix doesn't stick" pattern that
 * already cost two iterations.
 */

/**
 * Resolve the series name shown in the chart legend.
 *
 * Precedence (first match wins):
 *   1. `widget.options.legend_label`   ← Builder's explicit rename
 *   2. `widget.axes.y_label`           ← legacy explicit Y-axis label
 *   3. `widget.format.label`           ← legacy format-bag label
 *   4. `widget.value_column`           ← e.g. "spend"
 *   5. `widget.title`                  ← human title
 *   6. first `SUM(...)` leaf inside `widget.formula`
 *   7. "Value"
 *
 * @param {object} widget The widget shape persisted on the dashboard.
 * @returns {string} A non-empty display label.
 */
export function deriveLegendName(widget) {
    const opts = (widget && widget.options) || {};
    if (typeof opts.legend_label === "string" && opts.legend_label.trim()) {
        return opts.legend_label.trim();
    }
    const axes = (widget && widget.axes) || {};
    if (typeof axes.y_label === "string" && axes.y_label.trim()) {
        return axes.y_label.trim();
    }
    const fmt = (widget && widget.format) || {};
    if (typeof fmt.label === "string" && fmt.label.trim()) {
        return fmt.label.trim();
    }
    if (widget && widget.value_column) return widget.value_column;
    const t = ((widget && widget.title) || "").trim();
    if (t) return t;
    const f = (widget && widget.formula) || "";
    const m = /(?:SUM|AVG|MIN|MAX|COUNT)\s*\(\s*(?:[a-zA-Z_][a-zA-Z0-9_]*\.)?([a-zA-Z_][a-zA-Z0-9_]*)\s*\)/i.exec(f);
    if (m && m[1]) return m[1];
    return "Value";
}


/**
 * Resolve the label shown on the chart tooltip on hover.
 *
 * Same ladder as {@link deriveLegendName}, but prefers
 * `widget.options.tooltip_label` so a widget can intentionally show a
 * different label in the tooltip than the legend chip.
 *
 * @param {object} widget
 * @returns {string}
 */
export function deriveTooltipName(widget) {
    const opts = (widget && widget.options) || {};
    if (typeof opts.tooltip_label === "string" && opts.tooltip_label.trim()) {
        return opts.tooltip_label.trim();
    }
    return deriveLegendName(widget);
}


/**
 * Convenience: returns `{ legend, tooltip }` in one go for callers that
 * always need both.
 */
export function deriveChartLabels(widget) {
    const legend = deriveLegendName(widget);
    const opts = (widget && widget.options) || {};
    const tt = (typeof opts.tooltip_label === "string" && opts.tooltip_label.trim())
        ? opts.tooltip_label.trim()
        : legend;
    return { legend, tooltip: tt };
}

/**
 * Centralized resolve request helper for chart/KPI resolution on the frontend.
 * Ensures consistent propagation of filters, time grain, and scope decoration.
 */
export function resolveWidget(api, dashboardId, widget, { filters, timeGrain, decorateFn, additionalParams } = {}) {
    const isCombo = widget.chart_type === "combo" || widget.chart_type === "composed";
    const hasBreakdown = (widget.dimensions && widget.dimensions.length > 0) || 
                         (widget.group_by && widget.group_by.length > 1) || 
                         (widget.options && widget.options.stacked);
    const hasSeries = Array.isArray(widget.series) && widget.series.length > 0;

    // Use monthly time grain by default for combo/series/breakdown time charts to avoid daily label clutter
    let effectiveTimeGrain = timeGrain;
    if (!effectiveTimeGrain && (isCombo || hasSeries || hasBreakdown)) {
        effectiveTimeGrain = "month";
    }

    const payload = {
        filters: filters || {},
        time_grain: effectiveTimeGrain || "auto",
        ...additionalParams,
    };

    const decorated = decorateFn ? decorateFn(payload) : payload;
    return api.post(`/dashboards/${dashboardId}/widgets/${widget.id}/resolve`, decorated);
}

